import React, { useContext, useEffect, useMemo, useRef } from 'react';

import { cssMap } from '@compiled/react';

import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import { SmartCardContext, SmartCardProvider } from '@atlaskit/link-provider';
import { Box, Stack } from '@atlaskit/primitives/compiled';

import type { LuminaContextType } from '../analytics/generated/analytics.types';
import { LuminaAnalyticsContext } from '../analytics/lumina-analytics-context';
import { useLuminaAnalyticsEvents } from '../analytics/use-lumina-analytics-events';

import type { LuminaInteractionHandler } from './interaction';
import { LuminaInteractionProvider } from './interaction-context';
import type { LuminaSourceClickHandler } from './on-source-click';
import { parseLuminaTagsToJSON } from './parser/tag-parser';
import { LuminaSection } from './section/lumina-section';
import type { LuminaSubAgentResponseType, ParsedLayout, ParsedSubAgentDataMap } from './types';
import { getFirstFoldableSectionIdx } from './utils';

/** Props for {@link LuminaLayoutContent}: full public props minus what {@link LuminaLayoutInner} consumes for analytics providers. */
type LuminaLayoutContentProps = Omit<LuminaLayoutProps, 'traceId' | 'spanId'>;

interface LuminaLayoutProps {
	bridgeProduct?: string;
	cloudId: string;
	isStreaming?: boolean;
	luminaSubAgentResponse?: LuminaSubAgentResponseType[];
	luminaTagResponse: string;
	/** Invoked for allowlisted, user-initiated interactions with Lumina components. */
	onInteraction?: LuminaInteractionHandler;
	/** Invoked when a user clicks a source-like Lumina element (`<link-list>` row, `<smart-link>` embed). */
	onSourceClick?: LuminaSourceClickHandler;
	orgId: string;
	requestingUserId: string;
	/** Identifier for the session/conversation in which this Lumina layout was rendered. */
	sessionId?: string;
	/** Controls how smart-link elements are rendered. 'embed' renders a hero embed card. */
	smartLinkRenderer?: 'embed';
	source: LuminaContextType['source'];
	sourceProduct?: string;
	spanId?: string;
	traceId?: string;
	xpcProduct?: string;
	xpcSubProduct?: string;
}

const styles = cssMap({
	containerStyles: {
		textAlign: 'left',
		width: '100%',
	},
});

type LuminaSmartCardContextOverrideProps = {
	bridgeProduct?: string;
	children: React.ReactNode;
	xpcProduct?: string;
	xpcSubProduct?: string;
};

const LuminaSmartCardContextOverride = ({
	bridgeProduct,
	children,
	xpcProduct,
	xpcSubProduct,
}: LuminaSmartCardContextOverrideProps): React.JSX.Element => {
	const parentContext = useContext(SmartCardContext);

	const value = useMemo(() => {
		if (!parentContext) {
			return parentContext;
		}

		return {
			...parentContext,
			bridgeProduct: bridgeProduct ?? parentContext.bridgeProduct,
			xpcProduct: xpcProduct ?? parentContext.xpcProduct,
			xpcSubProduct: xpcSubProduct ?? parentContext.xpcSubProduct,
		};
	}, [bridgeProduct, parentContext, xpcProduct, xpcSubProduct]);

	const smartCardIdentityKey = value
		? [value.bridgeProduct ?? '', value.xpcProduct ?? '', value.xpcSubProduct ?? ''].join(':')
		: undefined;

	if (!value) {
		return <>{children}</>;
	}

	return (
		<SmartCardContext.Provider key={smartCardIdentityKey} value={value}>
			{children}
		</SmartCardContext.Provider>
	);
};

/**
 * Inner component that renders the parsed layout.
 * Must be rendered inside both AnalyticsContext and LuminaAnalyticsContext so hooks
 * capture shared context data.
 */
const LuminaLayoutContent = ({
	bridgeProduct,
	source,
	luminaTagResponse,
	luminaSubAgentResponse,
	isStreaming = false,
	smartLinkRenderer,
	cloudId,
	orgId,
	requestingUserId,
	sourceProduct,
	onInteraction,
	onSourceClick,
	xpcProduct,
	xpcSubProduct,
	sessionId,
}: LuminaLayoutContentProps): React.JSX.Element => {
	const { fireEvent } = useLuminaAnalyticsEvents();
	// Refs for parser stability optimization
	const parsedAnswerRef = useRef<ParsedLayout>([]);
	const sectionHashCacheRef = useRef<Map<number, string>>(new Map());

	// Track which errors have already been reported to prevent duplicate events
	const reportedTagParseErrorRef = useRef(false);
	const reportedSubAgentErrorsRef = useRef<Set<string>>(new Set());

	// Parse Lumina tag response with error handling
	const { parsedAnswer, tagParseError } = useMemo((): {
		parsedAnswer: ParsedLayout;
		tagParseError: boolean;
	} => {
		try {
			const result = parseLuminaTagsToJSON(
				luminaTagResponse,
				parsedAnswerRef.current,
				sectionHashCacheRef.current,
			);
			// Update the ref after parsing for next render
			parsedAnswerRef.current = result;
			return { parsedAnswer: result, tagParseError: false };
			// eslint-disable-next-line no-unused-vars
		} catch (_error) {
			// Return empty layout on parse error
			return { parsedAnswer: [], tagParseError: true };
		}
	}, [luminaTagResponse]);

	const firstFoldableIndex = useMemo(
		() => getFirstFoldableSectionIdx(parsedAnswer),
		[parsedAnswer],
	);

	// Parse sub-agent responses once and create a lookup map
	// Returns both the map and any parse errors (pure computation)
	const { subAgentDataMap, subAgentParseErrors } = useMemo((): {
		subAgentDataMap: ParsedSubAgentDataMap;
		subAgentParseErrors: string[];
	} => {
		if (!luminaSubAgentResponse) {
			return { subAgentDataMap: {}, subAgentParseErrors: [] };
		}

		const map: ParsedSubAgentDataMap = {};
		const errors: string[] = [];

		luminaSubAgentResponse.forEach((response) => {
			try {
				if (response.sub_agent_name === 'PROJECT_HIGHLIGHTS') {
					map.projectHighlights = JSON.parse(response.data);
				} else if (response.sub_agent_name === 'ENTITY_RELATED_PEOPLE') {
					map.relatedPeople = JSON.parse(response.data);
				}
				// eslint-disable-next-line no-unused-vars
			} catch (_error) {
				errors.push(response.sub_agent_name);
			}
		});

		return { subAgentDataMap: map, subAgentParseErrors: errors };
	}, [luminaSubAgentResponse]);

	// Fire analytics events for sub-agent parse errors (only once per sub-agent name)
	useEffect(() => {
		subAgentParseErrors.forEach((ctx) => {
			if (!reportedSubAgentErrorsRef.current.has(ctx)) {
				reportedSubAgentErrorsRef.current.add(ctx);
				fireEvent('operational.lumina.errored', {
					errorType: 'subAgentParse',
					context: ctx,
				});
			}
		});
	}, [subAgentParseErrors, fireEvent]);

	// Fire analytics event for tag parse errors (only once per mount)
	useEffect(() => {
		if (tagParseError && !reportedTagParseErrorRef.current) {
			reportedTagParseErrorRef.current = true;
			fireEvent('operational.lumina.errored', {
				errorType: 'tagParse',
				context: null,
			});
		}
	}, [tagParseError, fireEvent]);

	return (
		<LuminaInteractionProvider onInteraction={onInteraction}>
			<SmartCardProvider
				bridgeProduct={bridgeProduct}
				xpcProduct={xpcProduct}
				xpcSubProduct={xpcSubProduct}
			>
				<LuminaSmartCardContextOverride
					bridgeProduct={bridgeProduct}
					xpcProduct={xpcProduct}
					xpcSubProduct={xpcSubProduct}
				>
					<Box xcss={styles.containerStyles} testId="lumina-layout-container">
						<Stack>
							{parsedAnswer &&
								parsedAnswer.length &&
								parsedAnswer.map((section, index) => {
									const isFoldable = section.attributes?.folded === '1';
									const defaultFoldedState = isFoldable
										? index === firstFoldableIndex
											? 'expanded'
											: 'collapsed'
										: undefined;

									return (
										<LuminaSection
											key={index}
											section={section}
											subAgentDataMap={subAgentDataMap}
											defaultFoldedState={defaultFoldedState}
											isStreaming={isStreaming}
											smartLinkRenderer={smartLinkRenderer}
											cloudId={cloudId}
											orgId={orgId}
											requestingUserId={requestingUserId}
											sourceProduct={sourceProduct}
											source={source}
											onSourceClick={onSourceClick}
											sessionId={sessionId}
										/>
									);
								})}
						</Stack>
					</Box>
				</LuminaSmartCardContextOverride>
			</SmartCardProvider>
		</LuminaInteractionProvider>
	);
};

/**
 * Outer component that sets up analytics providers with shared data.
 *
 * Two providers are used:
 *
 * 1. **`AnalyticsContext`** — provides `source` and `componentName` which are
 *    standard fields that `FabricAnalyticsListeners` extracts into the top-level
 *    GAS event (e.g. the `source` field at the root of the event).
 *
 * 2. **`LuminaAnalyticsContext`** — provides `packageName`, `packageVersion`,
 *    `traceId`, and `spanId` as shared attributes. These are injected directly
 *    into every event's `attributes` object by `useLuminaAnalyticsEvents`,
 *    because `FabricAnalyticsListeners` does NOT extract custom context fields
 *    into the final GAS payload — only standard fields like `source` survive.
 */
const LuminaLayoutInner = ({
	source,
	traceId,
	spanId,
	...rest
}: LuminaLayoutProps): React.JSX.Element => {
	// Standard AnalyticsContext — source is extracted to top-level by FabricAnalyticsListeners
	const analyticsContextData = useMemo(
		() => ({
			componentName: 'LuminaLayout',
			source,
		}),
		[source],
	);

	// Shared attributes injected into every event's attributes object
	const sharedAttributes = useMemo(
		() => ({
			packageName: process.env._PACKAGE_NAME_ || '',
			packageVersion: process.env._PACKAGE_VERSION_ || '',
			traceId: traceId ?? null,
			spanId: spanId ?? null,
		}),
		[traceId, spanId],
	);

	return (
		<AnalyticsContext data={analyticsContextData}>
			<LuminaAnalyticsContext.Provider value={sharedAttributes}>
				<LuminaLayoutContent source={source} {...rest} />
			</LuminaAnalyticsContext.Provider>
		</AnalyticsContext>
	);
};

const LuminaLayout: React.MemoExoticComponent<
	({
		luminaTagResponse,
		luminaSubAgentResponse,
		isStreaming,
	}: LuminaLayoutProps) => React.JSX.Element
> = React.memo(LuminaLayoutInner);

LuminaLayout.displayName = 'LuminaLayout';

export { LuminaLayout };
